import {
  BuilderContent,
  Content,
  fetchOneEntry,
  getBuilderSearchParams,
  isPreviewing,
} from '@builder.io/sdk-react';
import {
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import moment from 'moment';
import React from 'react';
import ReactMarkdown from 'react-markdown';

import { BUILDER_PUBLIC_API_KEY } from '@headway/builderio/utils';
import helixProseCss from '@headway/helix/assets/prose.css?url';
import { IconArrowRight } from '@headway/helix/icons/ArrowRight';
import { trackPageView } from '@headway/shared/utils/analytics';
import { VectorBackground } from '@headway/ui/landing/Background';
import {
  CopyBody,
  CopyCaption,
  CopyFooter,
  CopyTitle,
} from '@headway/ui/landing/Copy';
import { Divider } from '@headway/ui/landing/Divider';
import {
  HeroSection,
  Section,
  SectionContainer,
} from '@headway/ui/landing/Section';
import { theme } from '@headway/ui/theme';

import { BlogPostAvatar } from '../../../components/Blog/BlogPostAvatar';
import { ButtonLink } from '../../../components/ButtonLink';
import { Link } from '../../../components/Link';
import {
  BlogPostData,
  getPostData,
  MatterFileData,
} from '../../../lib/blogPosts';
import loaderWrapper from '../../.server/loader';
import { description, opengraph, title, twitter } from '../../services/seo';

export const links: LinksFunction = () => {
  return [
    {
      rel: 'stylesheet',
      href: helixProseCss,
    },
  ];
};

export default function BlogPostPage() {
  const { post } = useLoaderData<typeof loader>();

  if (!isLegacyPostData(post)) {
    if (!post) {
      throw new Error('Post not found');
    }

    const metadata = getPostMetadata(post as BuilderContent);

    return (
      <BlogPostTemplate metadata={metadata}>
        <div className="hlx-prose">
          <Content
            model="blog-post"
            apiKey={BUILDER_PUBLIC_API_KEY}
            content={post as BuilderContent}
            customComponents={[]}
          />
        </div>
      </BlogPostTemplate>
    );
  }

  return (
    <div>
      <div>
        <LegacyPage
          post={post.post}
          content={post.content}
          title={post.title}
          summary={post.summary}
          author={post.author}
          role={post.role}
          avatar={post.avatar}
          date={post.date}
        />
      </div>
    </div>
  );
}

export const meta: MetaFunction<typeof loader> = ({ data, location }) => {
  if (!data) {
    return [];
  }

  const { post } = data;

  const metadata = isLegacyPostData(post)
    ? post
    : getPostMetadata(post as BuilderContent);

  const seoDate = moment(metadata.date).format();

  return [
    title(metadata.title),
    description(`A post by ${metadata.author} on the Headway blog`),
    ...opengraph(),
    {
      property: 'article:author',
      content: metadata.author,
    },
    {
      property: 'article:published_time',
      content: seoDate,
    },
    {
      property: 'article:modified_time',
      content: seoDate,
    },
    ...twitter(),
    {
      'script:ld+json': {
        '@context': 'http://schema.org',
        '@type': 'Blog',
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': new URL(
            location.pathname,
            process.env.NEXT_PUBLIC_MARKETING_URL
          ),
        },
        headline: title,
        image: [],
        datePublished: seoDate,
        dateModified: seoDate,
        author: { '@type': 'Person', name: metadata.author },
        description: `A post by ${metadata.author} on the Headway blog`,
      },
    },
  ];
};

async function loaderImpl({ params, request }: LoaderFunctionArgs) {
  const slug = params.slug;

  if (typeof slug !== 'string') {
    throw new Error('Invalid slug');
  }

  /**
   * First we load the post data from the file system. This is mostly for legacy
   * posts, but new posts are loaded from Builder.io.
   */
  let legacyPost = getPostData(slug);

  if (legacyPost) {
    return {
      post: legacyPost,
    };
  }

  const url = new URL(request.url);

  const post = await fetchOneEntry({
    // https://builder.io/models/b543583ec3f94bd58ec987eb6f4e706b
    model: 'blog-post',
    apiKey: BUILDER_PUBLIC_API_KEY,
    options: getBuilderSearchParams(url.searchParams),
    userAttributes: { urlPath: url.pathname },
  });

  if (!post && !isPreviewing(url.search)) {
    throw new Response('Page Not Found', {
      status: 404,
      statusText: 'Page not found',
    });
  }

  return {
    post,
  };
}

export const loader = loaderWrapper(loaderImpl, {
  post: null as BuilderContent | null,
});

export function isLegacyPostData(post: any): post is BlogPostData {
  return 'content' in post;
}

export function getPostMetadata(post: BuilderContent): MatterFileData {
  const metadata: MatterFileData = {
    title: post.data?.title ?? '',
    summary: post.data?.summary,
    date: post.data?.publishedOn,
    author: post.data?.author?.value?.data.name,
    role: post.data?.author?.value?.data.title,
    avatar: post.data?.author?.value?.data.avatar,
  };

  return metadata;
}

function LegacyPage(props: BlogPostData) {
  React.useEffect(() => {
    trackPageView({
      name: `Blog Entry`,
      properties: {
        entryTitle: props.title,
      },
    });
  }, [props.title]);

  const metadata: MatterFileData = {
    title: props.title,
    author: props.author,
    date: props.date,
    summary: props.summary,
    avatar: props.avatar,
    role: props.role,
  };

  return (
    <BlogPostTemplate metadata={metadata}>
      <div
        css={{
          p: {
            fontSize: theme.fontSize.lg,
            lineHeight: 1.875,
            letterSpacing: '-.005em',
            marginBottom: theme.space.lg,
            fontFamily: theme.fontFamily.postGrotesk,
          },
          h2: {
            fontSize: theme.fontSize.xl2,
            fontFamily: theme.fontFamily.postGrotesk,
            fontWeight: theme.fontWeight.bold,
            marginTop: theme.space.xl4,
            marginBottom: theme.space.xl,
          },
          img: {
            marginTop: theme.space.base,
            marginBottom: theme.space.base,
          },
        }}
      >
        <ReactMarkdown
          children={props.content}
          components={{
            a: ({ node, ref, ...props }) => {
              //ReactMarkdown uses the legacy ref API, so we need to check for a string because
              // forwwardRef which Link uses doesn't accept strings as refs
              if (typeof ref === 'string') {
                ref = null;
              }

              return <Link ref={ref} {...props} target="_blank" />;
            },
          }}
        />
      </div>
    </BlogPostTemplate>
  );
}

interface BlogPostTemplateProps {
  children: React.ReactNode;
  metadata: MatterFileData;
}

export function BlogPostTemplate({
  children,
  metadata,
}: BlogPostTemplateProps) {
  return (
    <>
      <VectorBackground>
        <SectionContainer maxWidth={760}>
          <HeroSection centered={true}>
            <CopyCaption>{moment(metadata.date).format('LL')}</CopyCaption>
            <CopyTitle>{metadata.title}</CopyTitle>
            {metadata.author ? (
              <CopyBody>
                <BlogPostAvatar
                  src={metadata.avatar}
                  name={metadata.author}
                  role={metadata.role}
                />
              </CopyBody>
            ) : null}
          </HeroSection>
        </SectionContainer>
      </VectorBackground>
      <SectionContainer maxWidth={760}>
        <Section>{children}</Section>
      </SectionContainer>
      <Divider />
      <SectionContainer>
        <Section centered={true}>
          <CopyTitle>Join the team</CopyTitle>
          <CopyBody>
            We’re based in New York City, and growing every day. Come join us!
          </CopyBody>
          <CopyFooter>
            <ButtonLink
              color="primary"
              variant="contained"
              size="large"
              endIcon={<IconArrowRight />}
              href="/careers"
            >
              Join us
            </ButtonLink>
          </CopyFooter>
        </Section>
      </SectionContainer>
    </>
  );
}
